import { useCallback, useRef } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { getAmplitudeDeviceCategory } from '../utils/amplitudeEvents';

type TFieldEvent = React.FocusEvent<HTMLElement>;

/** Display "Step name" values (must match each step's stepLoaded event). */
type TSignupStepName = 'Get started' | 'Business details' | 'Personal details';

/**
 * Set on a field whose blur value doesn't reflect its real answer (e.g. a
 * combobox whose DOM value is free search text). The field reports completion
 * imperatively via `reportCompletion` instead; its blur is ignored here.
 */
const DEFER_COMPLETION_ATTR = 'data-defer-completion';

const isFieldComplete = (target: HTMLInputElement): boolean => {
  if (target.type === 'checkbox' || target.type === 'radio') {
    return target.checked;
  }
  return Boolean(target.value?.trim()) && target.checkValidity?.() !== false;
};

/**
 * Funnel-wide field-interaction tracking via event delegation.
 * Spread the returned `fieldProps` onto a step's <form>; child-field focus/blur
 * bubbles up (React onFocus/onBlur === focusin/focusout).
 * `step` must match the step's stepLoaded "Step name" value.
 *
 * `reportCompletion` lets a field whose DOM value diverges from its real answer
 * (a combobox backed by form state) emit its blur-equivalent completion event
 * through the same shared counters.
 */
export function useSignupFieldTracking(step: TSignupStepName) {
  const focusedThisVisit = useRef<Set<string>>(new Set());
  const completed = useRef<Set<string>>(new Set());
  const order = useRef<Map<string, number>>(new Map());

  const indexOf = (fieldName: string) => {
    if (!order.current.has(fieldName)) {
      order.current.set(fieldName, order.current.size);
    }
    return order.current.get(fieldName) as number;
  };

  const emitCompletion = useCallback(
    (fieldName: string, isComplete: boolean) => {
      if (isComplete) completed.current.add(fieldName);
      else completed.current.delete(fieldName);
      tracking.ampli.signupFieldInteracted({
        step,
        field_name: fieldName,
        field_index: indexOf(fieldName),
        interaction_type: 'blur',
        completed: isComplete,
        fields_completed: completed.current.size,
        client_device: getAmplitudeDeviceCategory(),
      });
    },
    [step],
  );

  const onFocus = useCallback(
    (event: TFieldEvent) => {
      const fieldName = (event.target as HTMLInputElement).name;
      if (!fieldName) return;
      if (focusedThisVisit.current.has(fieldName)) return;
      focusedThisVisit.current.add(fieldName);
      tracking.ampli.signupFieldInteracted({
        step,
        field_name: fieldName,
        field_index: indexOf(fieldName),
        interaction_type: 'focus',
        completed: false,
        fields_completed: completed.current.size,
        client_device: getAmplitudeDeviceCategory(),
      });
    },
    [step],
  );

  const onBlur = useCallback(
    (event: TFieldEvent) => {
      const target = event.target as HTMLInputElement;
      const fieldName = target.name;
      if (!fieldName) return;
      if (target.hasAttribute(DEFER_COMPLETION_ATTR)) return;
      emitCompletion(fieldName, isFieldComplete(target));
    },
    [emitCompletion],
  );

  return { fieldProps: { onFocus, onBlur }, reportCompletion: emitCompletion };
}
